import * as React from 'react';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArticleFormat, FORMAT_LABEL, Learning, Tally } from '../types';
import { Section, formatTime } from './common';

const cell = { py: 0.75 } as const;
const pct = (n: number | null) => (n === null ? '—' : `${n}%`);

function TallyRows({ rows }: { rows: [string, Tally, number | null][] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={cell} />
          <TableCell sx={cell}>Published</TableCell>
          <TableCell sx={cell}>Rejected</TableCell>
          <TableCell sx={cell}>Undone</TableCell>
          <TableCell sx={cell}>Approved as drafted</TableCell>
          <TableCell sx={cell}>Worked / didn’t / unclear</TableCell>
          <TableCell sx={cell}>Awaiting verdict</TableCell>
          <TableCell sx={cell}>Hit rate</TableCell>
          <TableCell sx={cell}>Results vs estimate</TableCell>
          <TableCell sx={cell}>Scoring weight</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(([label, t, weight]) => (
          <TableRow key={label}>
            <TableCell sx={{ ...cell, fontWeight: 600 }}>{label}</TableCell>
            <TableCell sx={cell}>{t.published}</TableCell>
            <TableCell sx={cell}>{t.rejected}</TableCell>
            <TableCell sx={cell}>{t.reverted ?? 0}</TableCell>
            <TableCell sx={cell}>{pct(t.cleanApprovalRate)}</TableCell>
            <TableCell sx={cell}>
              {t.worked} / {t.did_not_work} / {t.inconclusive}
            </TableCell>
            <TableCell sx={cell}>{t.awaitingVerdict}</TableCell>
            <TableCell sx={cell}>{pct(t.hitRate)}</TableCell>
            <TableCell sx={cell}>
              {t.actualVsExpected === null ? '—' : `${t.actualVsExpected}×`}
            </TableCell>
            <TableCell sx={cell}>
              {weight === null ? '—' : `×${weight}`}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function LearningPanel({ learning }: { learning: Learning }) {
  const { byType, byFormat } = learning.summary;
  const formats = Object.keys(FORMAT_LABEL) as ArticleFormat[];
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 820 }}>
        Every published decision gets a verdict at day 28 and a final one at day
        56. “Results vs estimate” compares real clicks with the expected number
        (1× = exactly as predicted). The one thing the system changes on its own
        is how it scores topics: once a format has 5 clear verdicts, its weight
        moves toward 0.5 + its hit rate (all worked → ×1.5, half → ×1, none →
        ×0.5), by at most 0.1 a week.
      </Typography>
      <Section title="By action">
        {Object.keys(byType).length ? (
          <TallyRows
            rows={(
              [
                ['New articles', byType.new_article],
                ['Title & description rewrites', byType.title_meta],
                ['Article refreshes', byType.refresh],
                ['Duplicate merges', byType.merge],
              ] as [string, Tally | undefined][]
            )
              .filter((r): r is [string, Tally] => !!r[1])
              .map(([label, t]) => [label, t, null])}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No decisions yet.
          </Typography>
        )}
      </Section>
      <Section title="New articles by format">
        <TallyRows
          rows={formats
            .filter(f => byFormat[f])
            .map(f => [FORMAT_LABEL[f], byFormat[f], learning.weights[f] ?? 1])}
        />
      </Section>
      <Section title="Changes it has made">
        {learning.history.length ? (
          learning.history
            .slice()
            .reverse()
            .map(c => (
              <Typography variant="body2" key={`${c.at}-${c.format}`}>
                {formatTime(c.at)}: {c.because}
              </Typography>
            ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            None yet. The first verdicts arrive 28 days after the first articles
            are published.
          </Typography>
        )}
      </Section>
    </Stack>
  );
}
